import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tabs, Tab, Chip
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Title, Tooltip, Legend, ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { API_URL } from '../../config';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement);

const hdrCell = {
  fontWeight: 700, textAlign: 'center', py: 1.5, px: 1.5,
  color: '#fff', fontSize: '0.82rem', textTransform: 'uppercase',
  letterSpacing: 0.4, borderRight: '1px solid rgba(255,255,255,0.2)',
  whiteSpace: 'nowrap',
  background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)',
};

const pickerSx = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, backgroundColor: '#fff',
    '&:hover fieldset': { borderColor: '#1b4332' },
    '&.Mui-focused fieldset': { borderColor: '#1b4332', borderWidth: '2px' },
  },
};
const popperSx = {
  '& .MuiPaper-root': { borderRadius: 2, boxShadow: '0 8px 24px rgba(27,67,50,0.15)' },
  '& .MuiPickersDay-root': {
    borderRadius: 1.5,
    '&.Mui-selected': { backgroundColor: '#1b4332', '&:hover': { backgroundColor: '#0f2419' } },
  },
};

const CHART_COLORS = [
  '#1b4332','#2d6a4f','#40916c','#52b788','#74c69d',
  '#95d5b2','#b7e4c7','#d8f3dc','#ffa726','#ff7043',
];

const ContainerLoadingView = ({ onBackClick }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleSearch = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/container/loadingsummary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      const dateInfo = apiData.data?.[0]?.[0] || {};
      const markets   = apiData.data?.[1] || [];
      const shipping  = apiData.data?.[2] || [];
      const monthly   = apiData.data?.[3] || [];
      if (markets.length === 0 && monthly.length === 0) {
        setError('No data found for this date range.');
        return;
      }
      setData({ markets, shipping, monthly, dateInfo });
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    if (!data) return;
    const { addTitleRow, addSubtitleRow, addHeaderRow, addDataRow, addTotalRow} = await import('../../utils/excelStyles');
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Plywood Software Report';
    const subtitle = `Generated: ${new Date().toLocaleString()}   |   ${fromDate.format('DD/MM/YYYY')} – ${toDate.format('DD/MM/YYYY')}`;

    // Sheet 1 — Market summary
    const ws1 = wb.addWorksheet('Market Summary', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws1.columns = [{ width: 28 }, { width: 18 }, { width: 22 }, { width: 14 }, { width: 14 }];
    addTitleRow(ws1, 'CONTAINER LOADING — MARKET SUMMARY', 5);
    addSubtitleRow(ws1, subtitle, 5);
    addHeaderRow(ws1, ['Market', 'Current Period', 'Previous Period', 'Growth', 'Growth %']);
    data.markets.forEach((r, i) => {
      const growth = (r.MTD_CONTAINER || 0) - (r.PREVMONTH_CONTAINER || 0);
      const pct = r.PREVMONTH_CONTAINER > 0 ? ((growth / r.PREVMONTH_CONTAINER) * 100).toFixed(1) + '%' : 'N/A';
      addDataRow(ws1, [r.MARKET || '', r.MTD_CONTAINER || 0, r.PREVMONTH_CONTAINER || 0, growth, pct], i, { boldCols: [1] });
    });

    // Sheet 2 — Shipping line
    const ws2 = wb.addWorksheet('By Shipping Line', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws2.columns = [{ width: 28 }, { width: 18 }];
    addTitleRow(ws2, 'CONTAINER LOADING — BY SHIPPING LINE', 2);
    addSubtitleRow(ws2, subtitle, 2);
    addHeaderRow(ws2, ['Shipping Line', 'Container Count']);
    data.shipping.forEach((r, i) => {
      addDataRow(ws2, [r.Shipping_Line || '', r.CONTAINER_COUNT || 0], i, { boldCols: [1] });
    });
    const totalShipping = data.shipping.reduce((s, r) => s + (r.CONTAINER_COUNT || 0), 0);
    addTotalRow(ws2, ['GRAND TOTAL', totalShipping]);

    // Sheet 3 — Monthly trend
    const ws3 = wb.addWorksheet('Monthly Trend', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws3.columns = [{ width: 18 }, { width: 12 }, { width: 18 }];
    addTitleRow(ws3, 'CONTAINER LOADING — MONTHLY TREND', 3);
    addSubtitleRow(ws3, subtitle, 3);
    addHeaderRow(ws3, ['Loading Month', 'Month', 'Container Count']);
    data.monthly.forEach((r, i) => {
      addDataRow(ws3, [r.LOADING_MONTH || '', r.MONTHS || '', r.NO_OF_CONTAINER || 0], i);
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      `Container_Loading_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.xlsx`);
  };

  // Summary stats
  const totalCurrent  = data ? data.markets.reduce((s, r) => s + (r.MTD_CONTAINER || 0), 0) : 0;
  const totalPrev     = data ? data.markets.reduce((s, r) => s + (r.PREVMONTH_CONTAINER || 0), 0) : 0;
  const growth        = totalCurrent - totalPrev;
  const growthPct     = totalPrev > 0 ? ((growth / totalPrev) * 100) : 0;
  const totalShipping = data ? data.shipping.reduce((s, r) => s + (r.CONTAINER_COUNT || 0), 0) : 0;

  // Format server date string as "1 Mar 2026" without timezone conversion
  const fmtSrvDate = (val) => {
    if (!val) return '';
    const s = val.toString().substring(0, 10);
    const [yr, mo, dy] = s.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${parseInt(dy)} ${months[parseInt(mo) - 1]} ${yr}`;
  };
  const currentLabel  = data?.dateInfo ? `${fmtSrvDate(data.dateInfo.CURRENT_FROM)} – ${fmtSrvDate(data.dateInfo.CURRENT_TO)}` : 'Current Period';
  const previousLabel = data?.dateInfo ? `${fmtSrvDate(data.dateInfo.PREV_FROM)} – ${fmtSrvDate(data.dateInfo.PREV_TO)}` : 'Previous Period';


  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" mb={2.5}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: '#1b4332' }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)', borderRadius: 2, p: 1.5, mr: 2, display: 'flex', alignItems: 'center' }}>
              <LocalShippingIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1b4332' }}>Container Loading - Client Wise</Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>Market and shipping line breakdown by date range</Typography>
            </Box>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
              <DatePicker label="From Date" value={fromDate} onChange={setFromDate}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true, sx: pickerSx }, popper: { sx: popperSx } }} />
              <DatePicker label="To Date" value={toDate} onChange={setToDate} minDate={fromDate}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true, sx: pickerSx }, popper: { sx: popperSx } }} />
              <Button variant="contained" size="large" startIcon={<SearchIcon />}
                disabled={loading || !fromDate || !toDate} onClick={handleSearch}
                sx={{ minWidth: { xs: '100%', md: 160 }, borderRadius: 2, fontWeight: 600, textTransform: 'none',
                  background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)', color: '#fff',
                  '&:hover': { background: 'linear-gradient(135deg, #0f2419 0%, #1b4332 100%)' } }}>
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={8}><CircularProgress size={48} sx={{ color: '#1b4332' }} /></Box>}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {data && (
        <>
          {/* Results bar */}
          <Card sx={{ mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <LocalShippingIcon sx={{ fontSize: 32, color: '#fff' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Container Loading Summary</Typography>
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      <Chip label={`${totalCurrent} containers (current)`} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: '#1b4332', fontWeight: 600 }} />
                      <Chip label={`${data.markets.length} markets`} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: '#1b4332', fontWeight: 600 }} />
                    </Box>
                  </Box>
                </Box>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExcelDownload}
                  sx={{ background: '#fff', color: '#1b4332', fontWeight: 600, textTransform: 'none', borderRadius: 2,
                    '&:hover': { background: '#f1f8f4' } }}>
                  Download Excel
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* KPI cards — centered */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 3 }}>
            {[
              { label: currentLabel,  value: totalCurrent, color: '#1b4332', icon: <LocalShippingIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> },
              { label: previousLabel, value: totalPrev,   color: '#2563eb', icon: <LocalShippingIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> },
              { label: 'Growth',         value: `${growth >= 0 ? '+' : ''}${growth}`, color: growth >= 0 ? '#059669' : '#dc2626',
                icon: growth >= 0 ? <TrendingUpIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> : <TrendingDownIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> },
              { label: 'Growth %',       value: `${growthPct >= 0 ? '+' : ''}${growthPct.toFixed(1)}%`, color: growthPct >= 0 ? '#059669' : '#dc2626',
                icon: growthPct >= 0 ? <TrendingUpIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> : <TrendingDownIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> },
              { label: 'Shipping Lines', value: data.shipping.length, color: '#7c3aed', icon: <LocalShippingIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> },
              { label: 'Total (Shipping)', value: totalShipping, color: '#b45309', icon: <LocalShippingIcon sx={{ fontSize: { xs: 28, md: 40 }, opacity: 0.8 }} /> },
            ].map(({ label, value, color, icon }) => (
              <Card key={label} sx={{
                borderRadius: 2,
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                color: '#fff',
                width: { xs: 'calc(50% - 8px)', sm: 160 },
                height: 140,
                flexShrink: 0,
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  {icon}
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{value}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>{label}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Charts — same layout as ContainerMonthWiseView */}
          {/* Chart 1: Market Bar — Current vs Previous */}
          <Card sx={{
            borderRadius: 2,
            height: { xs: '300px', md: '400px' },
            mb: 3, maxWidth: '100%'
          }}>
            <CardContent sx={{ height: '100%', p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1b4332', textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Containers by Market — {currentLabel} vs {previousLabel}
              </Typography>
              <Box sx={{ height: 'calc(100% - 40px)', overflowX: { xs: 'auto', md: 'visible' }, overflowY: 'hidden',
                '&::-webkit-scrollbar': { height: '8px' },
                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px' },
              }}>
                <Box sx={{ minWidth: { xs: '600px', md: '100%' }, height: '100%' }}>
                  <Bar data={{
                    labels: data.markets.map(r => r.MARKET || ''),
                    datasets: [
                      { label: currentLabel,  data: data.markets.map(r => r.MTD_CONTAINER || 0),
                        backgroundColor: 'rgba(27,67,50,0.8)', borderColor: 'rgba(27,67,50,1)', borderWidth: 1, maxBarThickness: 50 },
                      { label: previousLabel, data: data.markets.map(r => r.PREVMONTH_CONTAINER || 0),
                        backgroundColor: 'rgba(255,167,38,0.8)', borderColor: 'rgba(255,167,38,1)', borderWidth: 1, maxBarThickness: 50 },
                    ],
                  }} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { position: 'top', labels: { font: { size: 12 } } } },
                    scales: {
                      y: { beginAtZero: true, title: { display: true, text: 'Container Count', font: { size: 12 } }, ticks: { font: { size: 11 } } },
                      x: { ticks: { font: { size: 11 }, maxRotation: 45 } },
                    },
                  }} />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Chart 2: Monthly Trend Line — only shown when multiple months */}
          {data.monthly.length > 1 && (
          <Card sx={{
            borderRadius: 2,
            height: { xs: '300px', md: '400px' },
            mb: 3, maxWidth: '100%'
          }}>
            <CardContent sx={{ height: '100%', p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1b4332', textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Monthly Container Loading Trend
              </Typography>
              <Box sx={{ height: 'calc(100% - 40px)', overflowX: { xs: 'auto', md: 'visible' }, overflowY: 'hidden',
                '&::-webkit-scrollbar': { height: '8px' },
                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px' },
              }}>
                <Box sx={{ minWidth: { xs: '500px', md: '100%' }, height: '100%' }}>
                  <Line data={{
                    labels: data.monthly.map(r => r.MONTHS || r.LOADING_MONTH || ''),
                    datasets: [{
                      label: 'Container Count',
                      data: data.monthly.map(r => r.NO_OF_CONTAINER || 0),
                      borderColor: 'rgba(255,183,77,1)', backgroundColor: 'rgba(255,183,77,0.1)',
                      borderWidth: 3, fill: true, tension: 0.4,
                      pointBackgroundColor: 'rgba(255,183,77,1)', pointRadius: 6, pointHoverRadius: 8,
                    }],
                  }} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true, title: { display: true, text: 'Container Count', font: { size: 12 } }, grid: { color: 'rgba(0,0,0,0.1)' }, ticks: { font: { size: 11 } } },
                      x: { grid: { color: 'rgba(0,0,0,0.1)' }, ticks: { font: { size: 11 } } },
                    },
                  }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
          )}

          {/* Chart 3: Shipping Line Doughnut */}
          <Card sx={{
            borderRadius: 2,
            height: { xs: '300px', md: '400px' },
            mb: 3, maxWidth: '100%'
          }}>
            <CardContent sx={{ height: '100%', p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1b4332', textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Container Distribution by Shipping Line
              </Typography>
              <Box sx={{ height: 'calc(100% - 40px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ maxWidth: { xs: '260px', md: '360px' }, width: '100%', height: '100%' }}>
                  <Doughnut data={{
                    labels: data.shipping.map(r => r.Shipping_Line || 'Unknown'),
                    datasets: [{
                      data: data.shipping.map(r => r.CONTAINER_COUNT || 0),
                      backgroundColor: CHART_COLORS,
                      borderColor: '#fff', borderWidth: 2,
                    }],
                  }} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom', labels: { font: { size: 11 }, padding: 12 } },
                      tooltip: { callbacks: { label: (ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        const pct = ((ctx.parsed / total) * 100).toFixed(1);
                        return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
                      }}},
                    },
                  }} />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Data tables */}
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8f5e9' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, color: '#666' },
                  '& .MuiTab-root.Mui-selected': { color: '#1b4332' },
                  '& .MuiTabs-indicator': { backgroundColor: '#1b4332', height: 3 } }}>
                <Tab label={`By Market (${data.markets.length})`} />
                <Tab label={`By Shipping Line (${data.shipping.length})`} />
                <Tab label={`Monthly Trend (${data.monthly.length})`} />
              </Tabs>
            </Box>

            {/* Tab 0 — Market */}
            {activeTab === 0 && (
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Market', currentLabel, previousLabel, 'Growth', 'Growth %'].map(h => (
                        <TableCell key={h} sx={hdrCell}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.markets.map((r, i) => {
                      const g = (r.MTD_CONTAINER || 0) - (r.PREVMONTH_CONTAINER || 0);
                      const p = r.PREVMONTH_CONTAINER > 0 ? ((g / r.PREVMONTH_CONTAINER) * 100).toFixed(1) + '%' : 'N/A';
                      return (
                        <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9', '&:hover': { backgroundColor: '#e8f5e9' } }}>
                          <TableCell sx={{ fontWeight: 700, color: '#1b4332', fontSize: '0.85rem' }}>{r.MARKET}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 600, fontSize: '0.9rem' }}>{r.MTD_CONTAINER || 0}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontSize: '0.85rem' }}>{r.PREVMONTH_CONTAINER || 0}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 600, color: g >= 0 ? '#059669' : '#dc2626', fontSize: '0.85rem' }}>{g >= 0 ? '+' : ''}{g}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 600, color: g >= 0 ? '#059669' : '#dc2626', fontSize: '0.85rem' }}>{p}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 1 — Shipping Line */}
            {activeTab === 1 && (
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Shipping Line', 'Container Count'].map(h => (
                        <TableCell key={h} sx={hdrCell}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.shipping.map((r, i) => (
                      <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9', '&:hover': { backgroundColor: '#e8f5e9' } }}>
                        <TableCell sx={{ fontWeight: 600, color: '#1b4332', fontSize: '0.85rem' }}>{r.Shipping_Line || 'Unknown'}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: 700, fontSize: '0.9rem' }}>{r.CONTAINER_COUNT || 0}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: '#1b4332' }}>
                      <TableCell sx={{ fontWeight: 800, color: '#fff', fontSize: '0.85rem' }}>GRAND TOTAL</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: 800, color: '#a7f3d0', fontSize: '1rem' }}>{totalShipping}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 2 — Monthly */}
            {activeTab === 2 && (
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Loading Month', 'Month', 'Container Count'].map(h => (
                        <TableCell key={h} sx={hdrCell}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.monthly.map((r, i) => (
                      <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9', '&:hover': { backgroundColor: '#e8f5e9' } }}>
                        <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{r.LOADING_MONTH || ''}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.85rem' }}>{r.MONTHS || ''}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: 700, fontSize: '0.9rem' }}>{r.NO_OF_CONTAINER || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </>
      )}

      {/* Empty state */}
      {!data && !loading && !error && (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 3, border: '2px dashed #d0e8d5' }}>
          <LocalShippingIcon sx={{ fontSize: 60, color: '#c8e6c9', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#1b4332', fontWeight: 700 }}>No Data to Display</Typography>
          <Typography variant="body1" sx={{ color: '#666', mt: 1 }}>Select a date range and click Search</Typography>
        </Card>
      )}
    </Container>
  );
};

export default ContainerLoadingView;
